import qs from 'qs';
import { useEffect, useState } from 'react';
import useSWR, { SWRConfiguration, KeyedMutator } from 'swr';

import { buildQueryParams, generateApiPath, getCollectionPaginated } from '#app/Handler/api';
import { Collection, PathParam, RouteApi } from '#app/typings/api';
import { Pagination, Sorting } from '#app/typings/utils';

type HookReturn<CollectionType, Filters> = {
  data: CollectionType[];
  totalItems: number;
  loading: boolean;
  mutate: KeyedMutator<Collection<CollectionType>>;
  pagination: Pagination;
  sorting: Sorting;
  setPagination: (pagination: Pagination) => void;
  setSorting: (sorting: Sorting) => void;
  filters: Partial<Filters>;
  setFilters: (filters: Partial<Filters>) => void;
};

type View<Filters> = {
  filters?: Partial<Filters>;
  pagination?: Pagination;
  sorting?: Sorting;
};

const buildInitialView = <Filters>(defaultView: View<Filters>): View<Filters> => {
  const queryString = window.location.search.slice(1);
  const parsedParams = qs.parse(queryString) as Record<string, any>;

  const { page, limit, sortName, sortDirection, ...filters } = parsedParams;

  return {
    filters: {
      ...defaultView.filters,
      ...filters,
    },
    pagination: {
      page: parseInt(page) || defaultView?.pagination?.page || 0,
      limit: parseInt(limit) || defaultView?.pagination?.limit || 25,
    },
    sorting: {
      sortName: sortName || defaultView?.sorting?.sortName || '',
      sortDirection: (sortDirection as 'asc' | 'desc') || defaultView?.sorting?.sortDirection || 'asc',
    },
  };
};

export function useCollectionPaginated<CollectionType, Filters = undefined>(
  route?: RouteApi,
  defaultView?: View<Filters>,
  params: {
    [key in PathParam<RouteApi>]: string | null;
  } = {},
  config?: SWRConfiguration,
): HookReturn<CollectionType, Filters> {
  const {
    history,
    location: { search },
  } = window;
  const initialView = buildInitialView<Filters>(defaultView ?? {});
  const [filters, setFilters] = useState<Partial<Filters>>(initialView.filters ?? {});
  const [pagination, setPagination] = useState<Pagination>(initialView.pagination ?? {});
  const [sorting, setSorting] = useState<Sorting>(initialView.sorting ?? {});
  const { data, isLoading, mutate } = useSWR<Collection<CollectionType>>(
    route ? generateApiPath(route, params ?? {}, { ...pagination, ...sorting, ...filters }) : null,
    getCollectionPaginated,
    {
      keepPreviousData: true,
      revalidateOnFocus: pagination.page === 0,
      ...config,
    },
  );

  useEffect(() => {
    const historySearch = buildQueryParams({
      ...pagination,
      ...sorting,
      ...filters,
    });
    if ('?' + historySearch !== search) {
      const currentURL = new URL(window.location.href);
      history.replaceState(null, '', `${currentURL.origin}${currentURL.pathname}?${historySearch}`);
    }
  }, [pagination, sorting, filters, search]);

  return {
    data: data?.data ?? [],
    totalItems: data?.totalItems ?? 0,
    loading: isLoading,
    mutate,
    pagination,
    setPagination: (pagination) => setPagination(pagination),
    sorting,
    setSorting: (sorting) => setSorting(sorting),
    filters,
    setFilters: (filters) => setFilters(filters),
  };
}
